import { useState } from 'react';

import { apiForms } from 'services/apiService';

// Tipos para os diferentes endpoints de análise
export type AnalysisType =
  | 'completion-all'
  | 'completion-by-user'
  | 'match-by-common-neighbors'
  | 'connection-points'
  | 'report';

interface AnalysisParams {
  userId?: string;
  formId?: string;
  fromUserId?: string;
  toUserId?: string;
}

export function useAnalysis(type: AnalysisType, params: AnalysisParams = {}) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  console.log('[useAnalysis] type:', type);
  console.log('[useAnalysis] params:', params);

  const executeRequest = async () => {
    // Validação específica para cada tipo de análise
    const validateParams = () => {
      switch (type) {
        case 'completion-all':
          return true; // Não precisa de parâmetros
        case 'completion-by-user':
          return params.userId && params.userId.trim() !== '';
        case 'match-by-common-neighbors':
          return (
            params.formId &&
            params.formId.trim() !== '' &&
            params.userId &&
            params.userId.trim() !== ''
          );
        case 'connection-points':
          return (
            params.fromUserId &&
            params.fromUserId.trim() !== '' &&
            params.toUserId &&
            params.toUserId.trim() !== ''
          );
        case 'report':
          return params.formId && params.formId.trim() !== '';
        default:
          return false;
      }
    };

    if (!validateParams()) {
      console.log('[useAnalysis] Parâmetros inválidos para', type, ':', params);
      setData(null);
      setError('Parâmetros inválidos para esta análise');
      return;
    }

    setLoading(true);
    setError(null);

    // Construir a URL baseada no tipo
    let url = '/v1/analysis/';

    switch (type) {
      case 'completion-all':
        url += 'completion/all';
        break;
      case 'completion-by-user':
        url += `completion/${params.userId}`;
        break;
      case 'match-by-common-neighbors':
        url += `match-by-common-neighbors/${params.formId}/${params.userId}`;
        break;
      case 'connection-points':
        url += `report/connection-points?from_user_uuid=${params.fromUserId}&to_user_uuid=${params.toUserId}`;
        break;
      case 'report':
        url += `report/${params.formId}`;
        break;
      default:
        setError('Tipo de análise não suportado');
        setLoading(false);
        return;
    }

    console.log('[useAnalysis] Fazendo requisição para:', url);

    try {
      const res = await apiForms.get(url);
      console.log(`[useAnalysis] Resposta do endpoint ${type}:`, res.data);
      setData(res.data);
    } catch (err: any) {
      console.error(`[useAnalysis] Erro na requisição ${type}:`, err);
      setError(err.message || `Erro ao buscar análise ${type}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, executeRequest };
}

// Hooks específicos para facilitar o uso
export function useCompletionAll() {
  return useAnalysis('completion-all');
}

export function useCompletionByUser(userId: string) {
  return useAnalysis('completion-by-user', { userId });
}

export function useMatchByCommonNeighbors(formId: string, userId: string) {
  return useAnalysis('match-by-common-neighbors', { formId, userId });
}

export function useConnectionPoints(fromUserId: string, toUserId: string) {
  return useAnalysis('connection-points', { fromUserId, toUserId });
}

export function useReport(formId: string) {
  return useAnalysis('report', { formId });
}
